/**
 * PaymentRunsPanel — tick the payments that went out, batch them into a run,
 * then send the three reports: the supplier's bank-style notification, the
 * supervisor's batch summary and the bookkeeper's disbursement report.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Banknote, FileText, Loader2, Send, Settings, Plus, Trash2, Table2, ChevronLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API } from "@/App";
import { openAuthedDoc } from "@/lib/openAuthedDoc";

const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PaymentRunsPanel({ token, refreshKey = 0 }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [payments, setPayments] = useState([]);
  const [runs, setRuns] = useState([]);
  const [settings, setSettings] = useState(null);
  const [picked, setPicked] = useState([]);
  const [open, setOpen] = useState(null);         // open run
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [draft, setDraft] = useState({ name: "", run_date: new Date().toISOString().slice(0, 10), notes: "" });
  const [sendFor, setSendFor] = useState(null);   // {kind, group_key, label, to, cc, message, attach_pdf, attach_csv}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([
        axios.get(`${API}/payment-runs/payments`, { headers, params: { unbatched: true } }),
        axios.get(`${API}/payment-runs`, { headers }),
      ]);
      setPayments(p.data.payments || []);
      setRuns(r.data.runs || []);
      setSettings(r.data.settings || null);
    } catch { /* quiet */ } finally { setLoading(false); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load, refreshKey]);

  const openRun = async (rid) => {
    try {
      const r = await axios.get(`${API}/payment-runs/${rid}`, { headers });
      setOpen(r.data.run);
      setSettings(r.data.settings);
    } catch { toast.error("Could not open that run"); }
  };

  const createRun = async () => {
    if (!picked.length) { toast.error("Tick the payments that belong in this run"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/payment-runs`, { ...draft, payment_ids: picked }, { headers });
      toast.success(`${r.data.run.id} created — ${money(r.data.run.total)}`);
      setPicked([]);
      setOpen(r.data.run);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create the run"); } finally { setBusy(false); }
  };

  const removeRun = async (rid) => {
    if (!window.confirm("Delete this run? Only possible if nothing has been sent from it.")) return;
    try {
      await axios.delete(`${API}/payment-runs/${rid}`, { headers });
      setOpen(null); load();
      toast.success("Run deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete the run"); }
  };

  const saveSettings = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/payment-runs/settings`, settings, { headers });
      setSettings(r.data.settings);
      toast.success("Payment reporting settings saved");
      setShowSettings(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };

  const doSend = async () => {
    const to = sendFor.to.split(",").map((s) => s.trim()).filter(Boolean);
    if (!to.length) { toast.error("Add at least one recipient"); return; }
    setBusy(true);
    try {
      const url = sendFor.kind === "supervisor" ? "send-supervisor"
        : sendFor.kind === "bookkeeper" ? "send-bookkeeper" : "send-supplier-letter";
      const body = {
        to, cc: sendFor.cc.split(",").map((s) => s.trim()).filter(Boolean),
        message: sendFor.message, attach_pdf: sendFor.attach_pdf,
        attach_csv: !!sendFor.attach_csv,
      };
      if (sendFor.kind === "supplier") body.group_key = sendFor.group_key;
      const r = await axios.post(`${API}/payment-runs/${open.id}/${url}`, body, { headers });
      if (r.data.success) {
        toast.success(`${sendFor.label} sent`);
        setSendFor(null);
      } else {
        toast.error(r.data.send?.error || "The mail provider rejected the message");
      }
      openRun(open.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); } finally { setBusy(false); }
  };

  const openSend = (kind, label, defaultTo, group_key) => setSendFor({
    kind, label, group_key, to: defaultTo || "", cc: "", message: "",
    attach_pdf: true, attach_csv: kind === "bookkeeper",
  });

  // ------------------------------------------------------------- open a run
  if (open) {
    return (
      <section className="rounded-xl border border-slate-200 bg-white p-4" data-testid="payment-run-detail">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2">
            <Button size="sm" variant="ghost" onClick={() => { setOpen(null); load(); }} data-testid="run-back-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.14em] text-emerald-700">
                Payment run · {open.id}
              </p>
              <h3 className="text-base font-black text-slate-900">{open.name}</h3>
              <p className="text-xs text-slate-500">
                {open.run_date} · {open.invoice_count} invoice(s) · {open.supplier_count} supplier(s) ·{" "}
                <strong className="text-slate-900">{money(open.total)}</strong>
              </p>
            </div>
          </div>
          <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
            onClick={() => removeRun(open.id)} data-testid="run-delete-btn">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>

        <div className="overflow-x-auto mt-3">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-slate-50">
              <tr>
                {["Supplier", "Invoice", "Method", "Pay date", "Confirmation #", "Amount"].map((h) => (
                  <th key={h} className="text-left px-2 py-1.5 text-[9.5px] font-black uppercase tracking-wider text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(open.payments || []).map((p) => p.invoices.map((inv, k) => (
                <tr key={`${p.id}-${k}`} data-testid={`run-line-${p.id}-${k}`}>
                  <td className="px-2 py-1.5 text-[12px] font-semibold">{p.supplier_name}</td>
                  <td className="px-2 py-1.5 text-[11px] font-mono">{inv.invoice_number}</td>
                  <td className="px-2 py-1.5 text-[11px]">{p.method_label}</td>
                  <td className="px-2 py-1.5 text-[11px]">{p.payment_date}</td>
                  <td className="px-2 py-1.5 text-[11px] font-mono">{p.reference || "—"}</td>
                  <td className="px-2 py-1.5 text-[11px] font-black">{money(inv.amount)}</td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>

        <div className="grid md:grid-cols-3 gap-3 mt-4">
          <div className="rounded-lg border border-slate-200 p-3">
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Supervisor batch report</p>
            <p className="text-[11px] text-slate-500 mb-2">Every invoice paid, amount, date and confirmation number.</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="run-supervisor-pdf"
                onClick={() => openAuthedDoc(`/payment-runs/${open.id}/supervisor-report`, { token, fmt: "pdf" })}>
                <FileText className="w-3.5 h-3.5 mr-1" /> PDF
              </Button>
              <Button size="sm" className="h-7 text-xs bg-sky-700 hover:bg-sky-800" data-testid="run-supervisor-send"
                onClick={() => openSend("supervisor", "Supervisor report", settings?.supervisor_email)}>
                <Send className="w-3.5 h-3.5 mr-1" /> Send
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 p-3">
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Bookkeeper's report</p>
            <p className="text-[11px] text-slate-500 mb-2">Disbursements by invoice, PO, method and reference.</p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="run-bookkeeper-pdf"
                onClick={() => openAuthedDoc(`/payment-runs/${open.id}/bookkeeper-report`, { token, fmt: "pdf" })}>
                <FileText className="w-3.5 h-3.5 mr-1" /> PDF
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="run-bookkeeper-csv"
                onClick={() => openAuthedDoc(`/payment-runs/${open.id}/bookkeeper-report`, { token, fmt: "csv" })}>
                <Table2 className="w-3.5 h-3.5 mr-1" /> CSV
              </Button>
              <Button size="sm" className="h-7 text-xs bg-sky-700 hover:bg-sky-800" data-testid="run-bookkeeper-send"
                onClick={() => openSend("bookkeeper", "Bookkeeper report", settings?.bookkeeper_email)}>
                <Send className="w-3.5 h-3.5 mr-1" /> Send
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 p-3">
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Supplier notifications</p>
            <p className="text-[11px] text-slate-500 mb-2">Bank-style letter per supplier in this run.</p>
            <div className="space-y-1.5">
              {(open.groups || []).map((g) => (
                <div key={g.key} className="flex items-center justify-between gap-2" data-testid={`run-group-${g.key}`}>
                  <span className="text-[11.5px] font-semibold text-slate-800 truncate">
                    {g.supplier_name} · {money(g.total)}
                  </span>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="outline" className="h-7 text-xs"
                      onClick={() => openAuthedDoc(
                        `/payment-runs/${open.id}/supplier-letter/${encodeURIComponent(g.key)}`,
                        { token, fmt: "pdf" })}>
                      <FileText className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" className="h-7 text-xs bg-sky-700 hover:bg-sky-800"
                      onClick={() => openSend("supplier", `Letter to ${g.supplier_name}`, g.supplier_email, g.key)}>
                      <Send className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {!!(open.sends || []).length && (
          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="run-send-log">
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Sent from this run</p>
            {open.sends.map((s) => (
              <p key={s.id} className="text-[11px] text-slate-600">
                {new Date(s.at).toLocaleString()} · {s.kind} → {s.to.join(", ")}
                {s.sent ? <span className="text-emerald-700 font-semibold"> · sent</span>
                  : <span className="text-red-600 font-semibold"> · failed: {s.error}</span>}
              </p>
            ))}
          </div>
        )}

        <Dialog open={!!sendFor} onOpenChange={(v) => !v && setSendFor(null)}>
          <DialogContent className="max-w-lg" data-testid="run-send-dialog">
            <DialogHeader>
              <DialogTitle>{sendFor?.label}</DialogTitle>
              <DialogDescription>
                The report goes in the email body; attachments are optional.
              </DialogDescription>
            </DialogHeader>
            {sendFor && (
              <div className="space-y-2">
                <Input placeholder="To — comma separated" value={sendFor.to} data-testid="run-send-to"
                  onChange={(e) => setSendFor((s) => ({ ...s, to: e.target.value }))} />
                <Input placeholder="CC" value={sendFor.cc} data-testid="run-send-cc"
                  onChange={(e) => setSendFor((s) => ({ ...s, cc: e.target.value }))} />
                <Textarea rows={2} placeholder="Note at the top of the email (optional)" value={sendFor.message}
                  data-testid="run-send-message"
                  onChange={(e) => setSendFor((s) => ({ ...s, message: e.target.value }))} />
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <input type="checkbox" checked={sendFor.attach_pdf} data-testid="run-send-attach-pdf"
                    onChange={(e) => setSendFor((s) => ({ ...s, attach_pdf: e.target.checked }))} />
                  Attach the PDF as well as putting the report in the email body
                </label>
                {sendFor.kind === "bookkeeper" && (
                  <label className="flex items-center gap-2 text-xs text-slate-600">
                    <input type="checkbox" checked={sendFor.attach_csv} data-testid="run-send-attach-csv"
                      onChange={(e) => setSendFor((s) => ({ ...s, attach_csv: e.target.checked }))} />
                    Attach the CSV for the accounting system
                  </label>
                )}
                <div className="flex justify-end">
                  <Button size="sm" className="bg-sky-700 hover:bg-sky-800" onClick={doSend} disabled={busy}
                    data-testid="run-send-btn">
                    {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                    Send
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </section>
    );
  }

  // ----------------------------------------------------------------- list
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4" data-testid="payment-runs-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.14em] text-emerald-700">Disbursements</p>
          <h3 className="text-base font-black text-slate-900 inline-flex items-center gap-2">
            <Banknote className="w-4 h-4 text-emerald-700" /> Payments &amp; runs
          </h3>
          <p className="text-xs text-slate-500">
            Batch the payments you have recorded, then send the supplier notification, the
            supervisor summary and the bookkeeper's report.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowSettings(true)} data-testid="run-settings-btn">
          <Settings className="w-3.5 h-3.5 mr-1" /> Settings
        </Button>
      </div>

      <div className="mt-3">
        <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">
          Payments not yet in a run ({payments.length})
        </p>
        {loading ? (
          <p className="text-sm text-slate-400 inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </p>
        ) : !payments.length ? (
          <p className="text-sm text-slate-400 py-2" data-testid="run-payments-empty">
            Nothing unbatched. Record a payment on an invoice and it shows up here.
          </p>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="run-payment-list">
            {payments.map((p) => (
              <label key={p.id} className="flex items-center gap-3 py-2 cursor-pointer"
                data-testid={`run-payment-${p.id}`}>
                <input type="checkbox" checked={picked.includes(p.id)}
                  onChange={(e) => setPicked(e.target.checked
                    ? [...picked, p.id] : picked.filter((x) => x !== p.id))} />
                <span className="min-w-0 flex-1">
                  <span className="block text-[12.5px] font-bold text-slate-900">
                    {p.supplier_name} · {money(p.amount)}
                  </span>
                  <span className="block text-[11px] text-slate-500">
                    {p.method_label} · {p.payment_date} · {p.invoice_numbers}
                    {p.reference ? ` · ref ${p.reference}` : " · no confirmation number"}
                  </span>
                </span>
              </label>
            ))}
          </div>
        )}

        {!!picked.length && (
          <div className="mt-3 rounded-lg border-2 border-emerald-200 bg-emerald-50/40 p-3" data-testid="run-create-box">
            <p className="text-[11px] font-bold text-emerald-900 mb-2">
              {picked.length} payment(s) selected ·{" "}
              {money(payments.filter((p) => picked.includes(p.id)).reduce((s, p) => s + p.amount, 0))}
            </p>
            <div className="grid sm:grid-cols-3 gap-2">
              <Input className="h-8 text-xs bg-white" placeholder="Run name" value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} data-testid="run-name" />
              <Input className="h-8 text-xs bg-white" type="date" value={draft.run_date}
                onChange={(e) => setDraft((d) => ({ ...d, run_date: e.target.value }))} data-testid="run-date" />
              <Input className="h-8 text-xs bg-white" placeholder="Notes" value={draft.notes}
                onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))} data-testid="run-notes" />
            </div>
            <div className="flex justify-end mt-2">
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={createRun} disabled={busy}
                data-testid="run-create-btn">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
                Create payment run
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-4">
        <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Runs</p>
        {!runs.length ? (
          <p className="text-sm text-slate-400 py-2" data-testid="run-list-empty">No runs yet.</p>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="run-list">
            {runs.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 py-2 flex-wrap">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-900">{r.name}</p>
                  <p className="text-[11px] text-slate-500">
                    {r.id} · {r.run_date} · {r.invoice_count} invoice(s) · {r.supplier_count} supplier(s) ·{" "}
                    {money(r.total)}
                    {(r.sends || []).filter((s) => s.sent).length
                      ? ` · ${(r.sends || []).filter((s) => s.sent).length} report(s) sent` : " · nothing sent yet"}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => openRun(r.id)} data-testid={`run-open-${r.id}`}>
                  Open
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="run-settings-dialog">
          <DialogHeader>
            <DialogTitle>Payment reporting settings</DialogTitle>
            <DialogDescription>
              Who gets the reports, and which of our accounts the money leaves from.
            </DialogDescription>
          </DialogHeader>
          {settings && (
            <div className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Supervisor</label>
                  <Input className="h-8 text-xs" placeholder="Name" value={settings.supervisor_name || ""}
                    onChange={(e) => setSettings((s) => ({ ...s, supervisor_name: e.target.value }))} />
                  <Input className="h-8 text-xs mt-1" placeholder="Email" value={settings.supervisor_email || ""}
                    onChange={(e) => setSettings((s) => ({ ...s, supervisor_email: e.target.value }))}
                    data-testid="settings-supervisor-email" />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Bookkeeper</label>
                  <Input className="h-8 text-xs" placeholder="Name" value={settings.bookkeeper_name || ""}
                    onChange={(e) => setSettings((s) => ({ ...s, bookkeeper_name: e.target.value }))} />
                  <Input className="h-8 text-xs mt-1" placeholder="Email" value={settings.bookkeeper_email || ""}
                    onChange={(e) => setSettings((s) => ({ ...s, bookkeeper_email: e.target.value }))}
                    data-testid="settings-bookkeeper-email" />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">
                  Our bank accounts — the letter prints "reference number at {"{institution}"}"
                </label>
                <div className="space-y-1.5 mt-1">
                  {(settings.bank_accounts || []).map((a, i) => (
                    <div key={i} className="grid sm:grid-cols-5 gap-2 items-center" data-testid={`bank-row-${i}`}>
                      <Input className="h-8 text-xs" placeholder="Institution — Frost" value={a.institution || ""}
                        onChange={(e) => setSettings((s) => ({
                          ...s, bank_accounts: s.bank_accounts.map((x, j) => (j === i ? { ...x, institution: e.target.value } : x)),
                        }))} />
                      <Input className="h-8 text-xs" placeholder="Nickname" value={a.nickname || ""}
                        onChange={(e) => setSettings((s) => ({
                          ...s, bank_accounts: s.bank_accounts.map((x, j) => (j === i ? { ...x, nickname: e.target.value } : x)),
                        }))} />
                      <Input className="h-8 text-xs" placeholder="Account name" value={a.account_name || ""}
                        onChange={(e) => setSettings((s) => ({
                          ...s, bank_accounts: s.bank_accounts.map((x, j) => (j === i ? { ...x, account_name: e.target.value } : x)),
                        }))} />
                      <Input className="h-8 text-xs" placeholder="Last 4" value={a.last4 || ""}
                        onChange={(e) => setSettings((s) => ({
                          ...s, bank_accounts: s.bank_accounts.map((x, j) => (j === i ? { ...x, last4: e.target.value } : x)),
                        }))} />
                      <div className="flex items-center gap-2">
                        <label className="text-[11px] text-slate-600 inline-flex items-center gap-1">
                          <input type="checkbox" checked={!!a.default}
                            onChange={(e) => setSettings((s) => ({
                              ...s, bank_accounts: s.bank_accounts.map((x, j) => ({ ...x, default: j === i ? e.target.checked : false })),
                            }))} />
                          Default
                        </label>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500"
                          onClick={() => setSettings((s) => ({
                            ...s, bank_accounts: s.bank_accounts.filter((_, j) => j !== i),
                          }))}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="bank-add"
                    onClick={() => setSettings((s) => ({
                      ...s, bank_accounts: [...(s.bank_accounts || []),
                      { institution: "", nickname: "", account_name: "", last4: "", default: false }],
                    }))}>
                    <Plus className="w-3 h-3 mr-1" /> Bank account
                  </Button>
                </div>
              </div>

              <div className="flex justify-end">
                <Button size="sm" className="bg-sky-700 hover:bg-sky-800" onClick={saveSettings} disabled={busy}
                  data-testid="run-settings-save">
                  {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null} Save settings
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
